import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import MapComponent from '../components/MapComponent';

const JobDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user: authUser } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applicationData, setApplicationData] = useState({
    cv_url: '',
    cover_letter: '',
  });

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await jobsAPI.getById(id);
        setJob(response.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load job details');
        setLoading(false);
      }
    };
    fetchJob();
    window.scrollTo(0, 0);
  }, [id]);

  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      alert('Please sign in to apply.');
      return;
    }
    try {
      await jobsAPI.apply(id, applicationData);
      alert('Application submitted successfully!');
      setApplicationData({ cv_url: '', cover_letter: '' });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit application');
    }
  };

  const formatSalary = (min, max) => {
    if (!min && !max) return 'Salary Undisclosed';
    if (min && max) return `$${(min/1000).toFixed(0)}k - $${(max/1000).toFixed(0)}k/yr`;
    return min ? `From $${(min/1000).toFixed(0)}k/yr` : `Up to $${(max/1000).toFixed(0)}k/yr`;
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <p className="mt-4 text-on-surface-variant font-medium tracking-tight">Curating opportunity...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="bg-error-container text-on-error-container p-8 rounded-3xl border border-error/10">
          <h2 className="text-2xl font-bold mb-2">Notice</h2>
          <p>{error || 'This job opportunity could not be found.'}</p>
          <button onClick={() => navigate('/jobs')} className="mt-6 font-bold underline">Return to job board</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background text-on-background antialiased selection:bg-primary-fixed selection:text-on-primary-fixed">
      <main className="pt-32 pb-24 max-w-7xl mx-auto px-6">
        {/* Job Header Section */}
        <header className="mb-16">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-8 bg-surface-container-lowest p-10 rounded-2xl shadow-2xl shadow-emerald-900/5 relative overflow-hidden border border-outline-variant/10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -mr-20 -mt-20 blur-3xl"></div>
            <div className="relative z-10 flex flex-col md:flex-row gap-8 items-start">
              <div className="w-20 h-20 bg-surface-container-low rounded-2xl flex items-center justify-center overflow-hidden border border-outline-variant/10">
                <img 
                  className="w-full h-full object-cover" 
                  alt="Company Logo"
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuBMgkmxOobs2JkF0GEFT9lRXHj1gtBRpiwqFb8eqXmyel5ZNq91qatKfnMpxREssNvNdBVNhgxCJ18VKewTwt5N1GNrcWmRM9vk-2N9naxkzCJ_hkAiqabTJe3atXfCnxqyWq9xmVUiZfO6wy03WQ94IWGeg3Ywu-DtNnZmc5yPchHQYMk1DrRDE07b5CmBmHStoXhm2MKIOd-Lm-Wcw3YR-dzb6SCuqoEmHK9bi_R02HW0JoHAzodnd4jaWVnsTowLU0E6o4CYs8Q"
                />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-on-surface mb-3">{job.title}</h1>
                <div className="flex flex-wrap items-center gap-4 text-on-surface-variant font-medium">
                  <span className="flex items-center gap-1.5"><span className="material-symbols-outlined text-primary scale-90">business</span> {job.company_name}</span>
                  <span className="text-outline-variant">•</span>
                  <span className="flex items-center gap-1.5"><span className="material-symbols-outlined text-primary scale-90">location_on</span> {job.location_address || 'Romania'}</span>
                  <span className="text-outline-variant">•</span>
                  <span className="text-primary font-bold">{formatSalary(job.salary_min, job.salary_max)}</span>
                </div>
              </div>
            </div>
            <div className="relative z-10 flex gap-4 w-full md:w-auto">
              <a href="#apply-form" className="flex-1 md:flex-none bg-primary text-on-primary px-10 py-4 rounded-full font-bold text-lg hover:bg-primary-container transition-all shadow-lg shadow-primary/20 active:scale-95 text-center">Apply Now</a>
              <button className="bg-surface-container-low text-primary p-4 rounded-full hover:bg-surface-variant transition-colors border border-outline-variant/10">
                <span className="material-symbols-outlined">bookmark</span>
              </button>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Main Content Area */}
          <div className="lg:col-span-8 space-y-16">
            {/* Editorial Take */}
            <section className="relative">
              <div className="bg-primary/5 border-l-4 border-primary p-8 rounded-r-2xl">
                <h2 className="text-sm font-bold uppercase tracking-widest text-primary mb-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-base">auto_awesome</span> The Editorial Take
                </h2>
                <p className="text-xl font-medium text-on-surface leading-relaxed italic">
                  "{job.company_name} is scaling their operations in {job.location_address || 'Bucharest'}. This role is a unique chance to join a team redefining the local industry standards, sitting directly at the intersection of innovation and community impact."
                </p>
              </div>
            </section>

            {/* Description */}
            <section>
              <h2 className="text-3xl font-bold text-on-surface mb-8">Job Description</h2>
              <div className="text-on-surface-variant leading-relaxed text-lg whitespace-pre-wrap">
                {job.description}
              </div>
            </section>

            {/* Requirements */}
            {job.required_skills && job.required_skills.length > 0 && (
              <section>
                <h2 className="text-3xl font-bold text-on-surface mb-8">Requirements</h2>
                <div className="bg-surface-container-low rounded-2xl p-8 grid grid-cols-1 md:grid-cols-2 gap-8 border border-outline-variant/10">
                  <div>
                    <h3 className="font-bold text-primary mb-4">Core Skills</h3>
                    <div className="flex flex-wrap gap-2">
                      {job.required_skills.map((skill, idx) => (
                        <span key={idx} className="px-3 py-1 bg-white border border-outline-variant/30 rounded-lg text-sm font-medium">{skill}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-primary mb-4">Languages</h3>
                    <p className="text-on-surface-variant">
                      {job.required_languages?.join(', ') || 'English, Romanian'}
                    </p>
                  </div>
                </div>
              </section>
            )}

            {/* Benefits */}
            <section>
              <h2 className="text-3xl font-bold text-on-surface mb-8">Benefits</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-6 bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10 flex flex-col items-center text-center gap-3">
                  <span className="material-symbols-outlined text-primary text-3xl">medical_services</span>
                  <span className="font-semibold">Premium Healthcare</span>
                </div>
                <div className="p-6 bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10 flex flex-col items-center text-center gap-3">
                  <span className="material-symbols-outlined text-primary text-3xl">flight_takeoff</span>
                  <span className="font-semibold">Flexible PTO</span>
                </div>
                <div className="p-6 bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10 flex flex-col items-center text-center gap-3">
                  <span className="material-symbols-outlined text-primary text-3xl">laptop_mac</span>
                  <span className="font-semibold">Work Equipment</span>
                </div>
              </div>
            </section>

            {/* Application Form */}
            <section className="mt-20 pt-20 border-t border-outline-variant/20" id="apply-form">
              <h2 className="text-3xl font-bold text-on-surface mb-4">Apply for this Position</h2>
              <p className="text-on-surface-variant mb-10">Fast-track your application by filling out our curated form below.</p>
              
              {isAuthenticated ? (
                <form onSubmit={handleApplicationSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-on-surface ml-1">Full Name</label>
                      <input 
                        className="w-full bg-white border-0 ring-1 ring-outline-variant/30 rounded-xl px-5 py-4 focus:ring-2 focus:ring-primary outline-none transition-all" 
                        defaultValue={authUser?.full_name}
                        required
                        type="text"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-on-surface ml-1">CV / Portfolio Link</label>
                      <input 
                        className="w-full bg-white border-0 ring-1 ring-outline-variant/30 rounded-xl px-5 py-4 focus:ring-2 focus:ring-primary outline-none transition-all" 
                        placeholder="https://yourportfolio.com" 
                        value={applicationData.cv_url}
                        onChange={(e) => setApplicationData({...applicationData, cv_url: e.target.value})}
                        required
                        type="url"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-on-surface ml-1">Why are you a great fit?</label>
                    <textarea 
                      className="w-full bg-white border-0 ring-1 ring-outline-variant/30 rounded-xl px-5 py-4 focus:ring-2 focus:ring-primary outline-none transition-all" 
                      placeholder="Tell us about your most impactful project..." 
                      rows="4"
                      value={applicationData.cover_letter}
                      onChange={(e) => setApplicationData({...applicationData, cover_letter: e.target.value})}
                      required
                    ></textarea>
                  </div>
                  <div className="flex items-center justify-end">
                    <button className="bg-primary text-on-primary px-12 py-4 rounded-full font-bold shadow-lg shadow-primary/20 hover:bg-primary-container transition-all active:scale-95" type="submit">Submit Application</button>
                  </div>
                </form>
              ) : (
                <div className="bg-surface-container-low p-10 rounded-3xl text-center border border-outline-variant/10">
                  <p className="text-lg text-on-surface-variant mb-8 font-medium">Please sign in to your Emerald Community account to apply for this position.</p>
                  <button onClick={() => navigate('/')} className="bg-primary text-on-primary px-12 py-4 rounded-full font-bold shadow-lg shadow-primary/20 hover:bg-primary-container transition-all">
                    Sign In to Apply
                  </button>
                </div>
              )}
            </section>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-8">
            {/* Job Summary Card */}
            <div className="bg-surface-container-low rounded-2xl p-8 space-y-6 border border-outline-variant/10 sticky top-28">
              <h3 className="text-xl font-bold text-on-surface">Job Summary</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-outline-variant/10">
                  <span className="text-on-surface-variant flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">schedule</span> Type
                  </span>
                  <span className="font-bold">{job.job_type}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-outline-variant/10">
                  <span className="text-on-surface-variant flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">distance</span> Work Style
                  </span>
                  <span className="font-bold">Hybrid</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-on-surface-variant flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">event</span> Posted
                  </span>
                  <span className="font-bold">{getTimeAgo(job.created_at)}</span>
                </div>
              </div>

              {/* Recruiter Card */}
              <div className="bg-surface-container-lowest rounded-2xl p-8 shadow-2xl shadow-emerald-900/5 flex flex-col items-center text-center gap-4 border border-outline-variant/5">
                <img 
                  className="w-20 h-20 rounded-full object-cover border-4 border-primary-fixed" 
                  alt="Recruiter"
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuBLmn-LdQE8HrZ--99SQwKMe37Zq81zrkjixYHmPVENhATgRyfgkTD-A2UBTPbEkvxqk859krn3E1dySigw2zMxOrBG-wtFsLvnpdkwcvs5d4wW5rIv_rvCmnI7cOUY1yvewQCaAfrfhnJCtSVa5ihigiyLhP4j5RA71HKy6mB-H4t16woXHKaik-vVixrY-eHwklfgr00wimMBkAfAzMylgXVnot49jSmFWy43giHDLaSJFqAV5xjjPmkFu9ZG1cmcYU1y8-cbNlY"
                />
                <div>
                  <h4 className="font-bold text-lg">Sarah Jenkins</h4>
                  <p className="text-sm text-primary font-medium">Local Talent Advisor</p>
                </div>
                <p className="text-sm text-on-surface-variant">"I'm managing this role personally. Feel free to reach out with any questions!"</p>
                <button className="w-full py-3 rounded-full border border-primary text-primary font-bold hover:bg-primary/5 transition-colors">Message Sarah</button>
              </div>

              {/* About Company & Map */}
              {job.location_lat && job.location_lng && (
                <div className="bg-surface-container-lowest rounded-2xl overflow-hidden border border-outline-variant/10">
                  <div className="p-8">
                    <h3 className="text-xl font-bold text-on-surface mb-4">About {job.company_name}</h3>
                    <p className="text-sm text-on-surface-variant leading-relaxed line-clamp-3">
                      A key player in the local ecosystem, {job.company_name} is located in the vibrant {job.location_address}. They value high-agency individuals and community craftsmanship.
                    </p>
                  </div>
                  <div className="h-48 relative">
                    <MapComponent
                      latitude={parseFloat(job.location_lat)}
                      longitude={parseFloat(job.location_lng)}
                      locationName={job.location_address}
                    />
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default JobDetailPage;
